import _ from 'lodash';
import React from 'react';
import {useMemo} from 'react';

import * as Panel2 from './panel';
import {Spec as PanelTableSpec, PanelTableConfig} from './PanelTable';
import {PanelComp2} from './PanelComp';
import * as Types from './types';
import * as CG from './cg/graph';
import * as Op from './cg/ops';
import * as LLReact from './cg/cgreact';

import {Dropdown} from 'semantic-ui-react';

const inputType = {
  type: 'union' as const,
  members: [
    {
      type: 'file' as const,
      wbObjectType: 'partitioned-table' as const,
    },
    {
      type: 'array' as const,
      objectType: {
        type: 'file' as const,
        wbObjectType: 'partitioned-table' as const,
      },
    },
  ],
};

export interface WBPartitionedTable {
  parts_path: string;
}

type PanelWBPartitionedTableFileProps = Panel2.PanelProps<
  typeof inputType,
  PanelTableConfig
>;

type JoinStyle = 'left' | 'right' | 'outer' | 'inner';

const PanelWBPartitionedTableFile: React.FC<PanelWBPartitionedTableFileProps> = props => {
  const [leftJoinKey, setLeftJoinKey] = React.useState<string | null>(null);
  const [rightJoinKey, setRightJoinKey] = React.useState<string | null>(null);
  const [leftJoinKeyOptions, setLeftJoinKeyOptions] = React.useState<{
    [key: string]: string;
  } | null>(null);
  const [rightJoinKeyOptions, setRightJoinKeyOptions] = React.useState<{
    [key: string]: string;
  } | null>(null);
  const [comparisonJoinStyle, setComparisonJoinStyle] = React.useState<
    JoinStyle
  >('outer');

  const fileOrFiles = props.input.path;
  const tablesRowsWithUnknownType = useMemo(() => {
    const tables = Op.applyOpToOneOrMany(
      Op.opFilePartitionedTable,
      'file',
      fileOrFiles,
      {}
    );
    return Op.applyOpToOneOrManyUnsafe(
      Op.opPartitionedTableRows,
      'partitionedTable',
      tables,
      {}
    );
  }, [fileOrFiles]);
  const tableWithTypeProm = LLReact.useNodeWithServerType(
    tablesRowsWithUnknownType as any
  );
  const tablesRows = tableWithTypeProm.result;

  const validRightKeys = React.useCallback(() => {
    if (
      leftJoinKey !== null &&
      leftJoinKeyOptions !== null &&
      rightJoinKeyOptions !== null
    ) {
      return Object.entries(rightJoinKeyOptions)
        .filter(entry => entry[1] === leftJoinKeyOptions[leftJoinKey])
        .map(entry => entry[0]);
    }
    return [];
  }, [leftJoinKey, leftJoinKeyOptions, rightJoinKeyOptions]);

  const finalRows = useMemo(() => {
    if (tableWithTypeProm.loading) {
      return tablesRows;
    }
    if (fileOrFiles.type.type === 'array') {
      const castedTableRows = tablesRows as CG.OutputNode<{
        type: 'array';
        objectType: 'any';
      }>;
      // const fromOp = castedTableRows.fromOp;
      const objType = castedTableRows.type.objectType;

      if (leftJoinKeyOptions === null) {
        setLeftJoinKeyOptions(
          _.fromPairs(
            Object.entries(
              castedTableRows.fromOp.inputs['0'].type.objectType
                .propertyTypes as {[key: string]: string}
            ).filter(entry => Types.SORTABLE_TYPES.includes(entry[1]))
          )
        );
      }
      if (rightJoinKeyOptions === null) {
        setRightJoinKeyOptions(
          castedTableRows.fromOp.inputs['1'].type.objectType.propertyTypes
        );
      }
      if (leftJoinKey === null && leftJoinKeyOptions !== null) {
        const leftKeys = Object.keys(leftJoinKeyOptions);
        if (leftKeys.length > 0) {
          setLeftJoinKey(leftKeys[0]);
        }
      }
      const validRightKeysInner = validRightKeys();
      if (rightJoinKey === null && validRightKeysInner.length > 0) {
        setRightJoinKey(validRightKeysInner[0]);
      }

      if (leftJoinKey != null && rightJoinKey != null) {
        const arr1 = {...castedTableRows.fromOp.inputs['0']};
        const arr2 = {...castedTableRows.fromOp.inputs['1']};
        return Op.opJoin({
          arr1: arr1 as any,
          arr2: arr2 as any,
          join1Fn: Op.constFunction(
            Op.opPick({
              obj: CG.varNode(objType, 'x') as any,
              key: Op.constString(leftJoinKey),
            })
          ) as any,
          join2Fn: Op.constFunction(
            Op.opPick({
              obj: CG.varNode(objType, 'x') as any,
              key: Op.constString(rightJoinKey),
            })
          ) as any,
          alias1: Op.constString('0'),
          alias2: Op.constString('1'),
          leftOuter: Op.constBoolean(
            ['outer', 'left'].includes(comparisonJoinStyle)
          ),
          rightOuter: Op.constBoolean(
            ['outer', 'right'].includes(comparisonJoinStyle)
          ),
        });
      }
      return null;
    } else {
      setLeftJoinKeyOptions(null);
      setRightJoinKeyOptions(null);
      return tablesRows;
    }
  }, [
    tablesRows,
    tableWithTypeProm.loading,
    fileOrFiles.type.type,
    leftJoinKey,
    rightJoinKey,
    validRightKeys,
    leftJoinKeyOptions,
    rightJoinKeyOptions,
    comparisonJoinStyle,
  ]);
  if (tableWithTypeProm.loading) {
    return <div></div>;
  }
  return (
    <>
      {leftJoinKeyOptions !== null && rightJoinKeyOptions !== null && (
        <div>
          Compare tables by&nbsp;&nbsp;
          <Dropdown
            button
            options={[
              {
                text: 'outer joining',
                value: 'outer',
              },
              {
                text: 'left joining',
                value: 'left',
              },
              {
                text: 'right joining',
                value: 'right',
              },
              {
                text: 'inner joining',
                value: 'inner',
              },
            ]}
            value={comparisonJoinStyle}
            onChange={(e, data) => {
              setComparisonJoinStyle(data.value as JoinStyle);
            }}
          />
          left&nbsp;&nbsp;
          <Dropdown
            button
            options={Object.keys(leftJoinKeyOptions).map(o => {
              return {
                text: '0.' + o,
                value: o,
              };
            })}
            value={leftJoinKey || ''}
            onChange={(e, data) => {
              setLeftJoinKey((data.value as string) || null);
              setRightJoinKey(null);
            }}
          />
          on right&nbsp;&nbsp;
          <Dropdown
            button
            options={validRightKeys().map(o => {
              return {
                text: '1.' + o,
                value: o,
              };
            })}
            value={rightJoinKey || ''}
            onChange={(e, data) => {
              setRightJoinKey((data.value as string) || null);
            }}
          />
        </div>
      )}
      {finalRows && (
        <PanelComp2
          // TODO: This should be actual inputType, not allowed
          inputType={PanelTableSpec.inputType}
          input={{path: finalRows as any}}
          loading={false}
          panelSpec={PanelTableSpec}
          configMode={false}
          context={props.context}
          config={props.config}
          updateConfig={props.updateConfig}
          updateContext={props.updateContext}
        />
      )}
    </>
  );
};

export const Spec: Panel2.PanelSpec = {
  id: 'wb-partitioned-table',
  Component: PanelWBPartitionedTableFile,
  inputType,
};
